import {
  Button,
  ButtonGroup,
  Flex,
  HStack,
  NumberInput,
  NumberInputField,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { addDays, differenceInDays } from 'date-fns';
import { useLocation, useParams } from 'react-router-dom';

import type { AssignmentFormData } from '../../../types/assignmentTypes';
import CreateSwitchContainer from '@/client/components/admin/create/shared/layout/CreateSwitchContainer';
import Datepicker from '@/client/components/admin/input/datepicker/Datepicker';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function AssignmentDueDateForm() {
  const { id } = useParams();
  const location = useLocation();

  const { pathname } = location;

  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<AssignmentFormData>();

  const isInstanceEdit = pathname.includes('instance') && !!id;

  const { setDueDate, useCustomDueDate, settings } = watch();
  const { daysToComplete, dueFromHired, type } = settings;

  const renderDatepicker = type === WorkflowType.PointInTime;

  const hasCustomDueDateError = !!errors?.customDueDate;
  const hasDaysToCompleteError = !!errors?.settings?.daysToComplete;

  useEffect(() => {
    if (!setDueDate) {
      setValue('settings.daysToComplete', undefined);
    } else if (!daysToComplete) {
      setValue('settings.daysToComplete', 1);
    }

    if (!setDueDate || (daysToComplete && daysToComplete > 0)) {
      clearErrors('customDueDate');
      clearErrors('settings.daysToComplete');
    }
  }, [setDueDate, daysToComplete]);

  const presetDaysNotUsed =
    daysToComplete !== null &&
    daysToComplete !== 10 &&
    daysToComplete !== 30 &&
    daysToComplete !== 90;

  const dateButtonStyles = {
    border: '1px solid',
    borderRadius: '6px',
    padding: 4,
    fontSize: '14px',
    fontWeight: 500,
    _hover: {
      backgroundColor: 'warmNeutral.0',
    },
  };

  const selectedButtonStyles = {
    ...dateButtonStyles,
    backgroundColor: 'warmNeutral.0',
    borderColor: 'warmNeutral.700',
    color: 'baseBlack',
  };

  const unselectedButtonStyles = {
    ...dateButtonStyles,
    backgroundColor: 'white',
    borderColor: 'warmNeutral.200',
    color: 'neutral.900',
  };

  return (
    <Stack spacing={4} paddingBottom={6} borderBottom="1px solid" borderBottomColor="neutral.100">
      <CreateSwitchContainer
        name="setDueDate"
        label={t('admin.assignments.settings.setDueDate')}
        helpText={t('admin.assignments.settings.setDueDate.helpText')}
      />

      {setDueDate && (
        <Stack spacing={4}>
          <Flex alignItems="center" justifyContent="space-between">
            <ButtonGroup>
              {renderDatepicker && (
                <Button
                  sx={
                    useCustomDueDate ? { ...selectedButtonStyles } : { ...unselectedButtonStyles }
                  }
                  onClick={() => {
                    setValue('useCustomDueDate', true);
                    setValue('settings.dueFromHired', false);
                  }}
                >
                  {t('admin.assignments.settings.dueDate.selectDate')}
                </Button>
              )}

              <>
                <Tooltip
                  isDisabled={!isInstanceEdit}
                  label={t('admin.assignments.edit.instance.assigningDaysDisabled')}
                >
                  <Button
                    isDisabled={isInstanceEdit}
                    sx={
                      !useCustomDueDate && !dueFromHired
                        ? { ...selectedButtonStyles }
                        : { ...unselectedButtonStyles }
                    }
                    onClick={() => {
                      setValue('settings.daysToComplete', 10);
                      setValue('useCustomDueDate', false);
                      setValue('settings.dueFromHired', false);
                    }}
                  >
                    {t('admin.assignments.settings.dueDate.daysFromAssigning')}
                  </Button>
                </Tooltip>

                <Button
                  sx={
                    !useCustomDueDate && dueFromHired
                      ? { ...selectedButtonStyles }
                      : { ...unselectedButtonStyles }
                  }
                  onClick={() => {
                    setValue('settings.daysToComplete', 10);
                    setValue('useCustomDueDate', false);
                    setValue('settings.dueFromHired', true);
                  }}
                >
                  {t('admin.assignments.settings.dueDate.daysFromHired')}
                </Button>
              </>
            </ButtonGroup>
          </Flex>

          {useCustomDueDate && renderDatepicker && (
            <Datepicker
              selectedDate={addDays(new Date(), daysToComplete || 0)}
              onChange={(date: Date) => {
                setValue('settings.daysToComplete', differenceInDays(date, new Date()) + 1);
              }}
              isDisabled={false}
              hasError={hasCustomDueDateError}
            />
          )}

          {!useCustomDueDate && (
            <HStack spacing={4}>
              <ButtonGroup>
                <Button
                  sx={
                    daysToComplete === 10
                      ? { ...selectedButtonStyles }
                      : { ...unselectedButtonStyles }
                  }
                  onClick={() => setValue('settings.daysToComplete', 10)}
                >
                  {t('admin.assignments.settings.dueDate.tenDays')}
                </Button>

                <Button
                  sx={
                    daysToComplete === 30
                      ? { ...selectedButtonStyles }
                      : { ...unselectedButtonStyles }
                  }
                  onClick={() => setValue('settings.daysToComplete', 30)}
                >
                  {t('admin.assignments.settings.dueDate.thirtyDays')}
                </Button>

                <Button
                  sx={
                    daysToComplete === 90
                      ? { ...selectedButtonStyles }
                      : { ...unselectedButtonStyles }
                  }
                  onClick={() => setValue('settings.daysToComplete', 90)}
                >
                  {t('admin.assignments.settings.dueDate.ninetyDays')}
                </Button>
              </ButtonGroup>

              <NumberInput
                variant="create"
                defaultValue={daysToComplete || 1}
                value={daysToComplete || 1}
                onChange={(valueString) => {
                  // Only allow positive integers
                  const value = parseInt(valueString, 10);

                  if (valueString === '') {
                    setValue('settings.daysToComplete', 1);
                  } else if (!Number.isNaN(value) && value > 0) {
                    setValue('settings.daysToComplete', value);
                  }
                }}
              >
                <NumberInputField
                  placeholder={`${t('admin.assignments.settings.dueDate.customAmount')}`}
                  borderColor={
                    hasDaysToCompleteError
                      ? 'error'
                      : presetDaysNotUsed
                      ? 'warmNeutral.700'
                      : 'warmNeutral.200'
                  }
                />
              </NumberInput>
            </HStack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
